import React, { useState, useMemo, useEffect } from "react";
import { STYLE_DEFAULT_TAB_BED } from "App/constants/theme";

import {
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@material-ui/core";
import {
  DatatablesFooter,
  IconButtonDatatable,
  MasterButton,
  ModalDefaultError,
  ModalDeleteConfirm,
  SearchInput,
  TableHeader,
  TableToolbar,
} from "App/Component/ui";

import { stableSort, getComparator } from "App/Component/ui/datatable/utils";
import { useSearchDatatables } from "App/hooks";
import {
  deleteKelasKamarById,
  getAllKelasKamar,
  headCells,
  ModalForm,
  RESET_STATE_KELAS_KAMAR,
} from "./helpers";
import { defaultDateDisplay } from "App/utils/helpers";
import { PencilIcon, TrashIcon } from "App/Assets";

const useStyles = STYLE_DEFAULT_TAB_BED;

let PageSize = 10;

export default function TabKelasKamar() {
  const classes = useStyles();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PageSize);

  const [showModal, setShowModal] = useState(false);
  const [showModalDefaultError, setShowModalDefaultError] = useState(false);
  const [showModalDeleteConfirm, setShowModalDeleteConfirm] = useState(false);

  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);

  const [formData, setFormData] = useState(RESET_STATE_KELAS_KAMAR);

  const [run, setRun] = useState(false);
  const { data, length } = useSearchDatatables(rows, q, ["name"]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useEffect(() => {
    (async () => {
      const res = await getAllKelasKamar();
      if (res) {
        setRows(res);
      } else {
        setShowModalDefaultError(true);
      }
    })();
  }, [run]);

  useMemo(() => {
    const firstPageIndex = (page - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    return data.slice(firstPageIndex, lastPageIndex);
  }, [page, data]);

  return (
    <div>
      {showModal && (
        <ModalForm
          run={() => setRun(!run)}
          setShowModal={() => setShowModal(false)}
          formData={formData}
          setFormData={setFormData}
        />
      )}

      {showModalDefaultError && (
        <ModalDefaultError
          setShowModalDefaultError={() => setShowModalDefaultError(true)}
        />
      )}

      {showModalDeleteConfirm && (
        <ModalDeleteConfirm
          confirmHandle={() =>
            deleteKelasKamarById(
              formData.id,
              () => setRun(!run),
              setShowModalDeleteConfirm,
              setFormData
            )
          }
          setShowModalDeleteConfirm={() => setShowModalDeleteConfirm(false)}
        />
      )}

      <Paper>
        <TableToolbar title={"Daftar Kelas Kamar"}>
          <SearchInput withIcon={true} onchange={(e) => setQ(e.target.value)} />
          <MasterButton
            type={"submit"}
            color="green"
            name="Tambah"
            size="md"
            onClick={() => {
              setFormData(RESET_STATE_KELAS_KAMAR);
              setShowModal(!showModal);
            }}
          />
          <MasterButton
            type={"submit"}
            color="warning"
            name="Export"
            size="md"
          />
        </TableToolbar>
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={"medium"}
            aria-label="enhanced table"
          >
            <TableHeader
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={length}
              headCells={headCells}
            />
            <TableBody>
              {stableSort(data, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={index}
                      style={{
                        backgroundColor:
                          index % 2 === 0 ? "transparent" : "#fff",
                      }}
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        padding="none"
                        align="center"
                      >
                        {index + 1}
                      </TableCell>

                      <TableCell
                        component="th"
                        scope="row"
                        padding="none"
                        align="center"
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "7px",
                          }}
                        >
                          <IconButtonDatatable
                            onClick={() => {
                              setFormData({
                                id: row?.id,
                                name: row?.name,
                                remark: row?.remark,
                                id_kelaskunjungan: row?.id_kelaskunjungan,
                                name_kelaskunjungan: row?.name_kelaskunjungan,
                              });

                              setShowModal(true);
                            }}
                          >
                            <PencilIcon />
                          </IconButtonDatatable>

                          <IconButtonDatatable
                            onClick={() => {
                              setFormData({
                                ...formData,
                                id: row?.id,
                              });
                              setShowModalDeleteConfirm(
                                !showModalDeleteConfirm
                              );
                            }}
                          >
                            <TrashIcon />
                          </IconButtonDatatable>
                        </div>
                      </TableCell>
                      <TableCell align="left">{row?.id}</TableCell>
                      <TableCell align="left">{row?.name}</TableCell>
                      <TableCell align="left">
                        {row?.nama_kelaskunjungan
                          ? row?.nama_kelaskunjungan
                          : " - "}
                      </TableCell>
                      <TableCell align="left">{row?.remark}</TableCell>
                      <TableCell align="left">
                        {defaultDateDisplay(row?.createdAt)}
                      </TableCell>
                      <TableCell align="left">{row?.createdName}</TableCell>
                      <TableCell align="left">
                        {defaultDateDisplay(row?.updatedAt)}
                      </TableCell>
                      <TableCell align="left">{row?.updatedName}</TableCell>
                    </TableRow>
                  );
                })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell align="center" colSpan={headCells.length}>
                    Tidak ada data
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <DatatablesFooter
          page={page}
          length={data.length}
          rowsPerPage={rowsPerPage}
          selectRows={[10, 20]}
          onPageChange={(page) => setPage(page - 1)}
          onSelectChange={(e) => setRowsPerPage(e.target.value)}
        />
      </Paper>
    </div>
  );
}
